// Treatment landscape for Alzheimer's / dementia — curated 2025–2026 from
// FDA, EMA, NICE, and Alzheimer's Association references. Reviewed by
// Ashlee Skabla Velez, APRN, ACNPC-AG.
//
// We tag each treatment with `availability` so we can softly note which
// countries have access. Live trials come from ClinicalTrials.gov via the
// backend /api/trials/search endpoint.

export const APPROVED_TREATMENTS = [
    {
        id: "lecanemab",
        name: "Lecanemab (Leqembi)",
        kind: "Disease-modifying — monoclonal antibody",
        approved: "FDA 2023 · EMA 2025 · PMDA (JP) 2023 · MHRA (UK) 2024",
        availability: ["US", "GB", "DE", "FR", "IE", "JP"],
        summary: "IV infusion every 2 weeks for adults with early Alzheimer's (MCI or mild dementia) who have amyloid plaques confirmed on PET or CSF. Slows decline by ~27% over 18 months in trials.",
        whoQualifies: "Confirmed early-stage Alzheimer's with biomarker evidence. APOE4 genotyping recommended before starting — increased ARIA risk.",
        sideEffects: "ARIA (brain swelling or microbleeds) in ~21%, infusion reactions, headache. Requires MRI monitoring at weeks 5, 7, 14, 26.",
        link: "https://www.alz.org/alzheimers-dementia/treatments/lecanemab-leqembi",
    },
    {
        id: "donanemab",
        name: "Donanemab (Kisunla)",
        kind: "Disease-modifying — monoclonal antibody",
        approved: "FDA 2024 · PMDA (JP) 2024 · MHRA (UK) 2024",
        availability: ["US", "JP", "GB"],
        summary: "IV infusion every 4 weeks for early Alzheimer's. Stops once amyloid is cleared (~12–18 months for many patients). Trials showed ~35% slower decline.",
        whoQualifies: "Early symptomatic Alzheimer's with confirmed amyloid. Excludes patients with 2 APOE4 alleles in some countries due to ARIA risk.",
        sideEffects: "ARIA in ~24%, infusion reactions. MRI monitoring required.",
        link: "https://www.alz.org/alzheimers-dementia/treatments/donanemab",
    },
    {
        id: "donepezil",
        name: "Donepezil (Aricept)",
        kind: "Cholinesterase inhibitor",
        approved: "Worldwide, since 1996",
        availability: ["US", "CA", "MX", "GB", "IE", "AU", "NZ", "DE", "FR", "ES"],
        summary: "Daily pill (5–23 mg). Boosts acetylcholine to help with memory, attention, daily function in mild to severe Alzheimer's. Modest, real benefit in ~half of patients.",
        whoQualifies: "Any stage of Alzheimer's. Often the first medication prescribed.",
        sideEffects: "Nausea, diarrhea, vivid dreams, slow heart rate. Take with food; consider switching to evening dosing if dreams disturb sleep.",
        link: "https://www.alz.org/alzheimers-dementia/treatments/medications-for-memory",
    },
    {
        id: "rivastigmine",
        name: "Rivastigmine (Exelon)",
        kind: "Cholinesterase inhibitor",
        approved: "Worldwide, since 2000",
        availability: ["US", "CA", "MX", "GB", "IE", "AU", "NZ", "DE", "FR", "ES"],
        summary: "Patch (4.6 / 9.5 / 13.3 mg/24h) or capsule. Especially useful for patients who can't reliably swallow pills. Also approved for Parkinson's dementia.",
        whoQualifies: "Mild to moderate Alzheimer's or Parkinson's dementia.",
        sideEffects: "Skin irritation at patch site (rotate sites daily), nausea, weight loss. Patch causes less GI upset than the pill.",
        link: "https://www.alz.org/alzheimers-dementia/treatments/medications-for-memory",
    },
    {
        id: "galantamine",
        name: "Galantamine (Razadyne)",
        kind: "Cholinesterase inhibitor",
        approved: "Worldwide, since 2001",
        availability: ["US", "CA", "GB", "IE", "AU", "DE", "FR", "ES"],
        summary: "Twice-daily pill or once-daily extended release. Same family as donepezil + rivastigmine — same modest benefit.",
        whoQualifies: "Mild to moderate Alzheimer's.",
        sideEffects: "Nausea, vomiting, diarrhea, weight loss. Take with food.",
        link: "https://www.alz.org/alzheimers-dementia/treatments/medications-for-memory",
    },
    {
        id: "memantine",
        name: "Memantine (Namenda)",
        kind: "NMDA-receptor antagonist",
        approved: "Worldwide, since 2003",
        availability: ["US", "CA", "MX", "GB", "IE", "AU", "NZ", "DE", "FR", "ES"],
        summary: "Daily pill or liquid (5–20 mg). Often added to donepezil for moderate-to-severe Alzheimer's. Helps with attention, agitation, daily activities.",
        whoQualifies: "Moderate to severe Alzheimer's. Often combined with a cholinesterase inhibitor.",
        sideEffects: "Dizziness, headache, confusion. Generally well-tolerated.",
        link: "https://www.alz.org/alzheimers-dementia/treatments/medications-for-memory",
    },
    {
        id: "brexpiprazole",
        name: "Brexpiprazole (Rexulti)",
        kind: "Atypical antipsychotic — for agitation",
        approved: "FDA 2023 (only country approved specifically for AD agitation)",
        availability: ["US"],
        summary: "First and only FDA-approved drug for agitation in Alzheimer's. Daily pill — used when non-drug approaches fail. Boxed warning for increased mortality in elderly.",
        whoQualifies: "Adults with moderate Alzheimer's experiencing clinically significant agitation.",
        sideEffects: "Sleepiness, weight gain, restlessness. Increased stroke + death risk in dementia patients — discuss carefully.",
        link: "https://www.alz.org/alzheimers-dementia/treatments/medications-for-behavior",
    },
];

export const NON_PHARMA = [
    {
        id: "exercise",
        name: "Regular aerobic exercise",
        summary: "150 min/week of brisk walking, swimming, or cycling. Strongest evidence-based non-drug intervention — slows cognitive decline and reduces falls. Start with whatever's enjoyable.",
    },
    {
        id: "mediterranean",
        name: "Mediterranean or MIND diet",
        summary: "Leafy greens, berries, fish 1–2×/week, olive oil, nuts. MIND-diet adherence is associated with up to 53% lower Alzheimer's risk in observational studies.",
    },
    {
        id: "sleep",
        name: "Treating sleep apnea",
        summary: "Untreated sleep apnea accelerates cognitive decline. CPAP therapy has been linked to improved attention and memory. Ask the PCP for a sleep study.",
    },
    {
        id: "hearing-aids",
        name: "Hearing aids",
        summary: "ACHIEVE 2023 trial showed hearing aids cut cognitive decline by 48% in at-risk older adults over 3 years. Single biggest modifiable risk factor.",
    },
    {
        id: "social",
        name: "Social engagement",
        summary: "Loneliness and isolation are risk factors. Memory cafés, adult day programs, weekly family calls — pick the smallest sustainable habit.",
    },
    {
        id: "cognitive-stimulation",
        name: "Cognitive Stimulation Therapy (CST)",
        summary: "14-session evidence-based group therapy. NICE-recommended in the UK. Search local memory clinics for a CST group.",
    },
    {
        id: "music",
        name: "Music therapy",
        summary: "Familiar music from age 18–25 calms agitation and unlocks memory. Personalized playlists (Music & Memory program) work better than generic playlists.",
    },
];

// Country-specific portals / orgs that connect families to trials and care.
export const HOW_TO_CONNECT = {
    US: [
        { name: "Alzheimer's Association TrialMatch", url: "https://www.alz.org/alzheimers-dementia/research_progress/clinical-trials/trialmatch", desc: "Free, personalized matching to studies. 250+ active trials." },
        { name: "ClinicalTrials.gov", url: "https://clinicaltrials.gov/search?cond=Alzheimer%20Disease&country=United%20States&aggFilters=status:rec", desc: "Official US federal registry of all trials, recruiting filter." },
        { name: "Alzheimer's Prevention Registry (GeneMatch)", url: "https://www.endalznow.org", desc: "Sign up to be considered for prevention studies if you're 50+." },
    ],
    CA: [
        { name: "Alzheimer Society Canada — Research", url: "https://alzheimer.ca/en/research/take-part-research", desc: "National portal for Canadian research studies." },
        { name: "Canadian Consortium on Neurodegeneration in Aging (CCNA)", url: "https://ccna-ccnv.ca", desc: "Canada-wide network of 350+ researchers + active trials." },
    ],
    MX: [
        { name: "Federación Mexicana de Alzheimer (FEDMA)", url: "https://www.fedma.mx", desc: "Federación nacional — directorio de centros de investigación y AMAES." },
        { name: "Instituto Nacional de Neurología y Neurocirugía", url: "https://www.innn.salud.gob.mx", desc: "Centro de referencia nacional con clínicas de memoria y ensayos." },
    ],
    GB: [
        { name: "Join Dementia Research (NHS)", url: "https://www.joindementiaresearch.nihr.ac.uk", desc: "NHS-backed register — sign up once, matched to studies UK-wide." },
        { name: "Alzheimer's Research UK", url: "https://www.alzheimersresearchuk.org/dementia-information/treatments-for-dementia/clinical-trials/", desc: "Trials directory + plain-English explainers." },
    ],
    IE: [
        { name: "Dementia Research Network Ireland", url: "https://dementianetwork.ie", desc: "Coordinated trials across Trinity College Dublin, UCD, NUIG." },
        { name: "Memory Research Unit (Trinity College / St James's)", url: "https://www.tcd.ie/medicine/memory-research-unit/", desc: "Active trial site for early-stage AD treatments." },
    ],
    AU: [
        { name: "StepUp for Dementia Research", url: "https://www.stepupfordementiaresearch.org.au", desc: "Australia's national volunteer register — University of Sydney." },
        { name: "Australian Dementia Network (ADNeT)", url: "https://www.australiandementianetwork.org.au", desc: "Memory clinic + trial network across all states." },
    ],
    NZ: [
        { name: "Brain Research New Zealand", url: "https://www.brainresearch.co.nz", desc: "Centre of Research Excellence — DAGNZ memory clinics + trials." },
        { name: "NZ Clinical Trials Registry (ANZCTR)", url: "https://www.anzctr.org.au", desc: "Search dementia studies recruiting in NZ + AU." },
    ],
    DE: [
        { name: "Deutsches Zentrum für Neurodegenerative Erkrankungen (DZNE)", url: "https://www.dzne.de", desc: "Nationales Netzwerk für Demenzforschung mit Studienzentren in 10 Städten." },
        { name: "DAlzG Studien-Datenbank", url: "https://www.deutsche-alzheimer.de/forschung/aktuelle-studien", desc: "Aktuelle Studien für Familien." },
    ],
    FR: [
        { name: "France Alzheimer — Recherche", url: "https://www.francealzheimer.org/la-recherche/", desc: "Annuaire des essais cliniques en France." },
        { name: "Centre Mémoire de Ressources et de Recherche (CMRR)", url: "https://www.has-sante.fr/jcms/c_2906096/fr/maladie-d-alzheimer-et-maladies-apparentees", desc: "Réseau national de centres mémoire — porte d'entrée pour les essais." },
    ],
    ES: [
        { name: "CIBERNED — Centro de Investigación Biomédica en Red", url: "https://www.ciberned.es", desc: "Red nacional de investigación en enfermedades neurodegenerativas." },
        { name: "Fundación Pasqual Maragall", url: "https://fpmaragall.org/es/estudios-de-investigacion", desc: "Estudio Alfa + ensayos de prevención de Alzheimer en España." },
    ],
};
